import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEventListener, useAccountData } from "../../../Hooks";
import { setPrivatePageAlertScope } from "../../../Hooks/usePageAlert";
import { useAuth, useAuthStore } from "../../../Contexts/AuthContext";
import { isMockFidoToken } from "../../../Constants/webauthnMock";
import { getAuthToken, isAuthTokenExpired } from "../../../Services/authToken";
import { readJwtClaim } from "../../../Services/deviceIdentity";
import {
  claimSessionTab,
  isForeignSessionOwner,
  readSessionOwnerTabId,
  subscribeSessionTabClaims,
} from "../../../Services/sessionTabClaim";
import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { useSupport } from "../../../Contexts/SupportContext";
import { useColorMode } from "../../../Hooks/useColorMode";
import { Box, Drawer, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { Redirect, Route, Link as RouterLink, Switch, useLocation } from "react-router-dom";
import { IMenuRoute, routes } from '../../../Constants/Routes'
import {
  clearIbAccountPickPending,
  getStoredSelectedAccountId,
  IB_ACTIVE_ACCOUNT_CHANGED_EVENT,
  isIbAccountPickPending,
} from "../../../Constants/accountTypes";
import { isIbHomeSelectableAccount } from "../../../Utils/accountPixEligibility";
import { CompanyLogo } from "../../Atoms";
import { HeaderContent, MenuNavigation } from "../../Molecules";
import { CarteiraAccessGate } from "./CarteiraAccessGate";
import {
  shouldShowAccountPickLoadingGate,
  shouldShowCarteiraAccessGate,
  shouldShowChooseAccountGate,
} from "./carteiraAccessGateRules";
import {
  isInactiveAccountLifecycle,
  resolveAccountLifecycle,
} from "../../../Utils/accountLifecycle";
import { normalizeIbAccountId } from "../../../Utils/isSameIbAccount";
import { PageAlertRenderer } from "../../Molecules/PageAlert";
import { TransactionalPasswordModal } from "../../../Pages/Private/Account/TransactionalPasswordModal";
import { BaasInstanceProvider } from "../../../Contexts/BaasInstanceContext";
import { isBaasPath } from "../../../Pages/Private/BaasConsole/constants";
import { useIbProductFeaturesStore } from "../../../Stores/ibProductFeaturesStore";
import { isIbProductEnabled } from "../../../Utils/ibProductFeatures";
import { API_URL } from "../../../config";
import { fetchInstanceBranding } from "../../../Hooks/WhiteLabel";
import {
  ChevronLeft,
  Menu
} from '@mui/icons-material';

const PrivateRouter = ({ route }: { route: IMenuRoute }) => {

  const { hasPermission, user, isReadOnly } = useAuth();
  const features = useIbProductFeaturesStore((state) => state.features);
  const canAccessCarteira = user.canAccessCarteira !== false;

  if (!canAccessCarteira && !isBaasPath(route.path)) {
    return <Redirect to={'/baas'}/>
  }

  if (route.hideWhenReadOnly && isReadOnly) {
    return <Redirect to={canAccessCarteira ? '/' : '/baas'}/>
  }

  if(route.permission && !hasPermission(route.permission)) {
    return <Redirect to={canAccessCarteira ? '/' : '/baas'}/>
  }

  if (route.requiredFeature && !isIbProductEnabled(features, route.requiredFeature)) {
    return <Redirect to={canAccessCarteira ? '/' : '/baas'}/>
  }

  return route.component();
}

const PrivateApp = () => {
  const { state: accountData, loading: accountLoading, handleGET: fetchAccountGET } = useAccountData();
  const { user, logout } = useAuth();
  const { refreshUnread } = useSupport();
  const location = useLocation();

  const theme = useTheme();
  const { isDark } = useColorMode();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const [txPasswordGateOpen, setTxPasswordGateOpen] = useState(false);
  const initializedRef = useRef(false);
  const txPasswordGateShownRef = useRef(false);

  const sessionDisconnect = useCallback(() => {
    if (isMockFidoToken(user.token)) return;
    logout({ disconnected: true });
  }, [logout, user.token]);

  const displaceThisTab = useCallback(() => {
    if (isMockFidoToken(user.token)) return;
    if (!useAuthStore.getState().isAutenticated) return;
    logout({ disconnected: true, localOnly: true });
  }, [logout, user.token]);

  // Duplicate tab / second window: new tab claims the JWT, old tab UI-logs-out
  // without POST /auth/logout (server session stays valid for the new tab).
  useEffect(() => {
    if (!user.isAutenticated || isMockFidoToken(user.token) || !user.token) {
      return;
    }

    const unsubscribe = subscribeSessionTabClaims({
      onForeignClaim: displaceThisTab,
    });

    const claim = () => {
      if (!useAuthStore.getState().isAutenticated) return;
      claimSessionTab();
    };

    const onVisibleOrFocus = () => {
      if (document.visibilityState === "hidden") return;
      const owner = readSessionOwnerTabId();
      if (isForeignSessionOwner(owner)) {
        displaceThisTab();
        return;
      }
      claim();
    };

    claim();

    window.addEventListener("focus", onVisibleOrFocus);
    window.addEventListener("pageshow", onVisibleOrFocus);
    document.addEventListener("visibilitychange", onVisibleOrFocus);

    return () => {
      unsubscribe();
      window.removeEventListener("focus", onVisibleOrFocus);
      window.removeEventListener("pageshow", onVisibleOrFocus);
      document.removeEventListener("visibilitychange", onVisibleOrFocus);
    };
  }, [displaceThisTab, user.isAutenticated, user.token]);

  useEffect(() => {
    setPrivatePageAlertScope(true);
    return () => setPrivatePageAlertScope(false);
  }, []);

  // Refresh DNS/instance branding after login (uses /v1/mobile/branding when JWT present).
  useEffect(() => {
    if (!user.isAutenticated || isMockFidoToken(user.token)) return;
    fetchInstanceBranding({ authenticated: true }).catch(() => undefined);
  }, [user.isAutenticated, user.token]);

  useEffect(() => {
    const root = document.getElementById("root");
    const { documentElement } = document;

    documentElement.classList.add("private-app-active");
    document.body.classList.add("private-app-active");
    root?.classList.add("private-app-active");

    return () => {
      documentElement.classList.remove("private-app-active");
      document.body.classList.remove("private-app-active");
      root?.classList.remove("private-app-active");
    };
  }, []);

  useEffect(() => {
    if (!user.isAutenticated) {
      initializedRef.current = false;
      return;
    }

    if (isMockFidoToken(user.token)) return;

    // Always refresh /me on auth — lifecycle (suspend/deactivate) can change while
    // the tab stays open; one-shot cache left the gate stuck on "active".
    fetchAccountGET();
    if (!initializedRef.current) {
      initializedRef.current = true;
      refreshUnread({ notify: true, force: true });
    }
  }, [user.isAutenticated, user.token, fetchAccountGET, refreshUnread]);

  useEffect(() => {
    if (!user.isAutenticated) {
      txPasswordGateShownRef.current = false;
      setTxPasswordGateOpen(false);
      return;
    }
    if (isMockFidoToken(user.token)) return;
    if (isBaasPath(location.pathname)) return;
    if (user.canAccessCarteira === false) return;
    if (accountLoading?.GET) return;

    const qualified = accountData?.transactionalPasswordQualified === true;
    if (qualified) {
      setTxPasswordGateOpen(false);
      return;
    }
    if (!accountData?.userId) return;
    if (txPasswordGateShownRef.current) return;

    txPasswordGateShownRef.current = true;
    setTxPasswordGateOpen(true);
  }, [
    user.isAutenticated,
    user.token,
    user.canAccessCarteira,
    location.pathname,
    accountLoading?.GET,
    accountData?.userId,
    accountData?.transactionalPasswordQualified,
  ]);

  // Re-fetch profile when the selected IB account changes (switcher / activate).
  useEffect(() => {
    if (!user.isAutenticated || isMockFidoToken(user.token)) return undefined;

    const onAccountChanged = () => {
      fetchAccountGET();
    };

    window.addEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onAccountChanged);
    return () => {
      window.removeEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onAccountChanged);
    };
  }, [user.isAutenticated, user.token, fetchAccountGET]);

  // Soft refresh when the tab becomes visible again (console suspend in another tab).
  useEffect(() => {
    if (!user.isAutenticated || isMockFidoToken(user.token)) return undefined;

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      fetchAccountGET();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [user.isAutenticated, user.token, fetchAccountGET]);

  // Single-account login: drop the Conta picker flag once /me is ready.
  useEffect(() => {
    if (!user.isAutenticated) return;
    if (user.canAccessCarteira === false) {
      clearIbAccountPickPending();
      return;
    }
    if (!isIbAccountPickPending()) return;
    if (accountLoading?.GET || !accountData?.userId) return;
    const accounts = Array.isArray(accountData?.accounts) ? accountData.accounts : [];
    const selectable = accounts.filter((row) => isIbHomeSelectableAccount(row));
    if (selectable.length <= 1) {
      clearIbAccountPickPending();
    }
  }, [
    user.isAutenticated,
    user.canAccessCarteira,
    accountLoading?.GET,
    accountData?.userId,
    accountData?.accounts,
  ]);

  useEventListener("error-401", sessionDisconnect, window as any);

  useEffect(() => {
    if (!user.isAutenticated || isMockFidoToken(user.token) || !user.token) {
      return;
    }

    const base = (API_URL || "").replace(/\/$/, "");
    if (!base) return;

    const myJti = readJwtClaim(user.token, "jti");
    const myType = readJwtClaim(user.token, "device_type") || "browser";
    // Capture token for this connection lifetime; effect re-runs when user.token changes.
    const sessionToken = user.token;

    const connection = new HubConnectionBuilder()
      .withUrl(`${base}/hubs/session`, {
        // MobileService CORS is AllowAnyOrigin (*). SignalR defaults withCredentials=true,
        // which browsers reject with ACAO:* — negotiate then fails and looks like a reconnect storm.
        // Auth is JWT via Authorization / access_token, so cookies are not needed.
        withCredentials: false,
        accessTokenFactory: () => getAuthToken() || sessionToken,
      })
      // Bounded retries after a dropped connection (default policy: 0/2/10/30s then stop).
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on("force_logout", (payload?: { reason?: string; device_type?: string; jti?: string }) => {
      const slot = payload?.device_type;
      if (slot && slot !== myType) return;
      // Same jti = this tab (or a duplicate sharing the JWT). Ignore — tab claim
      // handles "new tab stays, old tab UI-logs-out" without revoking Redis.
      if (payload?.jti && myJti && payload.jti === myJti) return;
      // Session already replaced/revoked server-side. Do not POST /auth/logout
      // (old JWT may 401, or a race could delete the new browser slot).
      logout({ disconnected: true, localOnly: true, clearBrowser: true });
    });

    // Presence heartbeat: server TTL is ~45s; ping every 20s while connected.
    const PRESENCE_PING_MS = 20_000;
    let pingTimer: ReturnType<typeof setInterval> | undefined;

    const startPresencePing = () => {
      if (pingTimer) clearInterval(pingTimer);
      const sendPing = () => {
        if (connection.state === HubConnectionState.Connected) {
          connection.invoke("Ping").catch(() => undefined);
        }
      };
      sendPing();
      pingTimer = setInterval(sendPing, PRESENCE_PING_MS);
    };

    const stopPresencePing = () => {
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = undefined;
      }
    };

    connection.onreconnected(() => startPresencePing());
    connection.onclose(() => stopPresencePing());

    connection
      .start()
      .then(() => startPresencePing())
      .catch(() => undefined);

    return () => {
      stopPresencePing();
      connection.off("force_logout");
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop().catch(() => undefined);
      }
    };
  }, [logout, user.isAutenticated, user.token]);

  // Proactive client-side JWT exp check on tab focus (covers long idle with no API calls).
  useEffect(() => {
    const checkExpiry = () => {
      if (isMockFidoToken(user.token)) return;
      const token = getAuthToken() || user.token;
      if (token && isAuthTokenExpired(token)) {
        sessionDisconnect();
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") checkExpiry();
    };

    window.addEventListener("focus", checkExpiry);
    document.addEventListener("visibilitychange", onVisibility);
    checkExpiry();

    return () => {
      window.removeEventListener("focus", checkExpiry);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [sessionDisconnect, user.token]);

  const isCompactDesktop = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Grid column is 0 on mobile (drawer overlays). Paper must keep a real width
  // or the temporary Drawer opens invisibly (0px) — hamburger appears broken.
  const drawerPaperWidth = isMobile ? 280 : isCompactDesktop ? 220 : 240;
  const sidebarWidth = isMobile ? 0 : drawerPaperWidth;

  const boxStyles = useMemo(() => {
    if (isMobile) {
      return {
        gridTemplateAreas: `
          "header"
          "main"
        `,
        gridTemplateColumns: '1fr',
        gridTemplateRows: '56px minmax(0, 1fr)',
      };
    }

    return {
      gridTemplateAreas: `
        "drawer header"
        "drawer main"
      `,
      gridTemplateColumns: `minmax(0, ${sidebarWidth}px) minmax(0, 1fr)`,
      gridTemplateRows: '56px minmax(0, 1fr)',
    };
  }, [isMobile, sidebarWidth]);

  if (!user.isAutenticated) {
    return <Redirect to="/auth" />;
  }

  if (!user.accepted_terms) {
    return <Redirect to="/auth/accepted-terms" />;
  }

  // Console-only: keep session inside /baas (Carteira disabled).
  if (user.canAccessCarteira === false && !isBaasPath(location.pathname)) {
    return <Redirect to="/baas" />;
  }

  // Gate follows the *selected* IB account, not the person / primary row.
  // A suspended account must not lock other active accounts on the same login.
  const selectedAccountId = getStoredSelectedAccountId();
  const selectedNormalized = normalizeIbAccountId(selectedAccountId);
  const ibAccounts = Array.isArray(accountData?.accounts) ? accountData.accounts : [];
  const selectableIbAccounts = ibAccounts.filter((row) => isIbHomeSelectableAccount(row));
  const findIbAccountById = (wanted) => {
    const wantedNorm = normalizeIbAccountId(wanted);
    if (!wantedNorm) return null;
    return (
      ibAccounts.find((row) => {
        const id = normalizeIbAccountId(row?.id || row?.account_id);
        return id !== "" && id === wantedNorm;
      }) || null
    );
  };
  const selectedAccount =
    (selectedNormalized ? findIbAccountById(selectedNormalized) : null)
    || ibAccounts.find((row) => row?.set_app_active)
    || null;
  const carteiraLifecycle = resolveAccountLifecycle(
    selectedAccount || { lifecycle: accountData?.accountLifecycle },
  );
  const selectedAccountInactive = isInactiveAccountLifecycle(carteiraLifecycle);
  const showWaitingGate =
    accountData?.waitingAccount === true && !selectedAccountInactive;
  const pickPending = isIbAccountPickPending();
  const profileReady = Boolean(accountData?.userId);
  const showLoadingGate = shouldShowAccountPickLoadingGate({
    pickPending,
    profileReady,
    canAccessCarteira: user.canAccessCarteira,
    isBaasPath: isBaasPath(location.pathname),
  });
  const showChooseGate = shouldShowChooseAccountGate({
    pickPending,
    selectableCount: selectableIbAccounts.length,
    selectedAccountInactive,
    showWaitingGate,
  });
  // Loading while pick-pending + /me not ready; choose only when profile ready and 2+ accounts.
  const showCarteiraGate = shouldShowCarteiraAccessGate({
    canAccessCarteira: user.canAccessCarteira,
    isBaasPath: isBaasPath(location.pathname),
    profileReady,
    selectedAccountInactive,
    showWaitingGate,
    showChooseGate,
    showLoadingGate,
  });

  if (showCarteiraGate) {
    const gateKind = showLoadingGate
      ? "loading"
      : selectedAccountInactive
        ? "suspended"
        : showWaitingGate
          ? "waiting"
          : "choose";
    return (
      <CarteiraAccessGate
        kind={gateKind}
        onLogout={logout}
        initialAccounts={ibAccounts}
        canAccessConsole={user.canAccessConsole === true}
        userName={accountData?.userName || ""}
      />
    );
  }

  return (
    <BaasInstanceProvider>
    <Box
      className="private-app h-screen min-h-0 min-w-0 bg-gray-50 dark:bg-brand-dark-bg"
      display="grid"
      overflow="hidden"
      sx={{ minWidth: 0, minHeight: 0, width: "100%", maxWidth: "100%", ...boxStyles }}
    >
      <Box
        gridArea="header"
        className="relative z-40 flex h-14 items-center overflow-visible border-b border-gray-100 bg-white px-3 dark:border-brand-dark-border dark:bg-brand-dark-surface min-[480px]:px-4 md:px-6"
        sx={{ boxShadow: "none" }}
      >
        {isMobile && (
          <>
            <IconButton
              onClick={() => setOpen(true)}
              aria-label="Abrir menu"
              sx={{
                mr: 0.5,
                flexShrink: 0,
                width: 44,
                height: 44,
                color: isDark ? "#e5e7eb" : undefined,
              }}
            >
              <Menu sx={{ fontSize: 22 }} />
            </IconButton>
            <RouterLink to="/" className="mr-2 flex shrink-0 items-center min-[480px]:mr-4">
              <CompanyLogo width={88} onDark={isDark} variant={isDark ? "gold" : "gray"} />
            </RouterLink>
          </>
        )}
        <div className="min-w-0 flex-1">
          <HeaderContent logout={logout} accountData={accountData} />
        </div>
      </Box>

      <Drawer
        onClose={() => setOpen(false)}
        variant={isMobile ? 'temporary' : 'permanent'}
        anchor="left"
        ModalProps={
          isMobile
            ? { keepMounted: true, sx: { zIndex: 1300 } }
            : undefined
        }
        sx={{
          gridArea: 'drawer',
          width: sidebarWidth,
          maxWidth: sidebarWidth,
          flexShrink: 0,
          minWidth: 0,
          minHeight: 0,
          height: '100%',
          zIndex: isMobile ? 1300 : undefined,
          '& .MuiDrawer-paper': {
            backgroundColor: isDark ? '#141414' : '#ffffff',
            borderRight: isDark ? '1px solid #262626' : '1px solid #e5e7eb',
            boxShadow: isMobile ? '0 8px 32px rgba(0,0,0,0.18)' : 'none',
            width: drawerPaperWidth,
            maxWidth: isMobile ? 'min(280px, 88vw)' : drawerPaperWidth,
            boxSizing: 'border-box',
            position: isMobile ? 'fixed' : 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        open={open || !isMobile}
      >
        <Box className="flex h-14 shrink-0 items-center border-b border-gray-100 px-4 dark:border-brand-dark-border">
          <RouterLink to="/" className="link-no-zoom flex shrink-0 items-center" onClick={() => isMobile && setOpen(false)}>
            <CompanyLogo width={120} onDark={isDark} variant={isDark ? "gold" : "gray"} />
          </RouterLink>
          {isMobile && (
            <IconButton
              aria-label="Fechar menu"
              onClick={() => setOpen(false)}
              sx={{
                ml: "auto",
                width: 44,
                height: 44,
                color: isDark ? "#e5e7eb" : undefined,
              }}
            >
              <ChevronLeft sx={{ fontSize: 22 }} />
            </IconButton>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
            px: 0.75,
            py: 1.5,
            scrollbarWidth: "thin",
            scrollbarColor: "#e5e7eb transparent",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#e5e7eb",
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#d1d5db",
            },
          }}
        >
          <MenuNavigation
            onNavigate={isMobile ? () => setOpen(false) : undefined}
          />
        </Box>
      </Drawer>

      <Box
        className="min-h-0 min-w-0 bg-white px-4 py-6 dark:bg-brand-dark-bg md:px-5 lg:px-8 lg:py-8"
        gridArea="main"
        component="main"
        sx={{
          width: "100%",
          minWidth: 0,
          minHeight: 0,
          maxWidth: "100%",
          overflowX: "hidden",
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        <div className="min-h-0 min-w-0 w-full max-w-full">
          <PageAlertRenderer priority={0} />
          <TransactionalPasswordModal
            open={txPasswordGateOpen}
            autoStartRegister
            onClose={() => setTxPasswordGateOpen(false)}
            onSuccess={() => {
              setTxPasswordGateOpen(false);
              fetchAccountGET();
            }}
          />
          <Switch>
            {routes.map(route => (
              <Route
                key={route.path}
                path={route?.path}
                exact={route?.exact ?? true}
              >
                <PrivateRouter route={route}/>
              </Route>
            ))}
          </Switch>
        </div>
      </Box>
    </Box>
    </BaasInstanceProvider>
  );
};

export default PrivateApp;
